/**
 * Typing Detection and Presence System
 * 
 * This module handles real-time typing detection and presence broadcasting
 * for collaborative document editing.
 */

import { getSupabase } from '../components/supabaseClient';
import { dbgLog } from './appDebugFlags';

const tdLog = (...args) => dbgLog('typing-detection', ...args);

class TypingDetection {
  constructor() {
    this.typingTimeout = null;
    this.isTyping = false;
    this.lastTypingTime = 0;
    this.typingThreshold = 1000; // 1 second
    this.presenceChannel = null;
    this.currentUser = null;
    this.currentDocument = null;
    this.currentEditor = null; // Store editor reference for focus checks
    this.supabase = null;
    this.lastBroadcastedTyping = null; // Track last broadcasted typing status
    this.presenceUpdateTimeout = null; // Debounce presence updates
    this.lastCursorUpdate = null; // Throttle cursor updates
    this.isInitialized = false; // Track initialization state
    this.lastTypingBroadcast = null; // Throttle typing broadcasts
    this.lastCursorMovement = null; // Throttle cursor movement
    this.documentLoadComplete = false; // Track if document has finished loading
    this.initialLoadTimeout = null; // Timeout for initial document load
    this.userHasInteracted = false; // Track if user has actually interacted with the document
  }

  /**
   * Initialize typing detection for a document
   * @param {Object} editor - Quill editor instance
   * @param {string} documentPath - Path to the document
   * @param {string} userId - Current user ID
   * @param {string} userName - Current user name
   */
  async initialize(editor, documentPath, userId, userName) {
    // Prevent multiple initializations for the same document
    if (this.isInitialized && this.currentDocument === documentPath) {
      // tdLog('⚠️ Typing detection already initialized for this document, skipping...');
      return;
    }
    
    // Clean up any existing instance first
    this.cleanup();
    
    this.currentDocument = documentPath;
    this.currentUser = { id: userId, name: userName };
    this.currentEditor = editor; // Store editor reference for focus checks
    this.supabase = await getSupabase();

    // Set up typing detection on editor
    this.setupTypingDetection(editor);
    
    // Set up presence channel
    await this.setupPresenceChannel();
    
    // Set up user interaction detection
    this.setupUserInteractionDetection(editor);
    
    // Set a timeout to mark document load as complete
    this.initialLoadTimeout = setTimeout(() => {
      this.documentLoadComplete = true;
      // tdLog('✅ Document load complete - typing detection now active');
    }, 5000); // 5 seconds delay to allow document to fully load
    
    this.isInitialized = true;
  }

  /**
   * Set up typing detection on the editor
   * @param {Object} editor - Quill editor instance
   */
  setupTypingDetection(editor) {
    if (!editor) return;

    // Listen for text changes
    editor.on('text-change', () => {
      this.handleTyping();
    });

    // Listen for selection changes (cursor movement)
    editor.on('selection-change', (range, oldRange, source) => {
      if (range) {
        // Cursor is in the document
        this.handleCursorMovement();
      } else {
        // Cursor is no longer in the document (user clicked away)
        this.handleCursorLeave();
      }
    });

    // Also listen for focus/blur events on the editor container
    const editorContainer = editor.container;
    if (editorContainer) {
      // When user clicks away from the editor container
      editorContainer.addEventListener('blur', () => {
        // Add a small delay to check if focus moved to another part of the document
        setTimeout(() => {
          if (!document.activeElement || !editorContainer.contains(document.activeElement)) {
            this.handleCursorLeave();
          }
        }, 100);
      });
      
      // When user clicks back into the editor
      editorContainer.addEventListener('focus', () => {
        this.handleCursorMovement();
      });
    }
  }

  /**
   * Set up user interaction detection
   * @param {Object} editor - Quill editor instance
   */
  setupUserInteractionDetection(editor) {
    if (!editor) return;

    const editorContainer = editor.container;
    if (editorContainer) {
      // Mark user interaction when they click on the editor
      editorContainer.addEventListener('click', () => {
        this.markUserInteraction();
      });

      // Mark user interaction when they start typing
      editorContainer.addEventListener('keydown', () => {
        this.markUserInteraction();
      });

      // Mark user interaction when they move the cursor
      editorContainer.addEventListener('mousedown', () => {
        this.markUserInteraction();
      });
    }
  }

  /**
   * Mark that the user has interacted with the document
   */
  markUserInteraction() {
    if (!this.userHasInteracted) {
      this.userHasInteracted = true;
      // tdLog('👆 User has interacted with the document - typing detection now enabled');
    }
  }

  /**
   * Handle typing events
   */
  handleTyping() {
    // Don't detect typing until document load is complete
    if (!this.documentLoadComplete) {
      // tdLog('⏳ Typing detection skipped - document still loading');
      return;
    }

    // Additional check: ensure we have a valid current user
    if (!this.currentUser || !this.currentUser.id) {
      // tdLog('⏳ Typing detection skipped - no current user');
      return;
    }

    // CRITICAL: Only detect typing if the user has actually interacted with the document
    if (!this.userHasInteracted) {
      // tdLog('⏳ Typing detection skipped - user has not interacted with document');
      return;
    }

    // Check if the document has focus and the user has clicked on it
    if (!document.hasFocus()) {
      // tdLog('⏳ Typing detection skipped - document not focused');
      return;
    }

    // Additional check: ensure the editor container has focus
    if (this.currentEditor && this.currentEditor.container) {
      const editorContainer = this.currentEditor.container;
      if (!editorContainer.contains(document.activeElement)) {
        // tdLog('⏳ Typing detection skipped - editor not focused');
        return;
      }
    }

    const now = Date.now();
    
    // Throttle typing detection to avoid spam
    if (this.lastTypingTime && (now - this.lastTypingTime) < 500) {
      return; // Skip if called within 500ms
    }
    
    this.lastTypingTime = now;

    // tdLog('🔍 User typing detected:');
    // tdLog('  - User ID:', this.currentUser?.id);
    // tdLog('  - User Name:', this.currentUser?.name);
    // tdLog('  - Is Typing:', this.isTyping);
    // tdLog('  - Will Broadcast:', !this.isTyping);

    if (!this.isTyping) {
      this.isTyping = true;
      this.broadcastTypingStatus(true);
    }

    // Clear existing timeout
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    // Set timeout to stop typing indicator
    this.typingTimeout = setTimeout(() => {
      // tdLog('⏰ Typing timeout reached - stopping typing indicator');
      this.isTyping = false;
      this.broadcastTypingStatus(false);
    }, this.typingThreshold);
    
    // Also set a longer timeout to ensure typing status is cleared even if the above fails
    setTimeout(() => {
      if (this.isTyping) {
        // tdLog('⏰ Extended typing timeout reached - force stopping typing indicator');
        this.isTyping = false;
        this.broadcastTypingStatus(false);
      }
    }, this.typingThreshold + 2000); // 2 seconds after the normal timeout
  }

  /**
   * Handle cursor movement
   */
  handleCursorMovement() {
    // Don't detect cursor movement until document load is complete
    if (!this.documentLoadComplete) {
      // tdLog('⏳ Cursor movement detection skipped - document still loading');
      return;
    }

    // Additional check: ensure we have a valid current user
    if (!this.currentUser || !this.currentUser.id) {
      // tdLog('⏳ Cursor movement detection skipped - no current user');
      return;
    }

    // CRITICAL: Only detect cursor movement if the user has actually interacted with the document
    if (!this.userHasInteracted) {
      // tdLog('⏳ Cursor movement detection skipped - user has not interacted with document');
      return;
    }

    // Check if the document has focus and the user has clicked on it
    if (!document.hasFocus()) {
      // tdLog('⏳ Cursor movement detection skipped - document not focused');
      return;
    }

    // Additional check: ensure the editor container has focus
    if (this.currentEditor && this.currentEditor.container) {
      const editorContainer = this.currentEditor.container;
      if (!editorContainer.contains(document.activeElement)) {
        // tdLog('⏳ Cursor movement detection skipped - editor not focused');
        return;
      }
    }

    // Throttle cursor movement to avoid spam
    const now = Date.now();
    if (this.lastCursorMovement && (now - this.lastCursorMovement) < 1000) {
      return; // Skip if called within 1000ms
    }
    this.lastCursorMovement = now;
    
    // Broadcast cursor position for real-time collaboration
    this.broadcastCursorPosition();
    
    // Update WebSocket awareness for cursor activity (but NOT typing)
    try {
      if (window.wsProvider && window.wsProvider.awareness) {
        window.wsProvider.awareness.setLocalStateField('cursor_active', true);
        // tdLog('🔌 WebSocket awareness cursor_active set to true');
      }
    } catch (error) {
      console.error('❌ Error setting WebSocket awareness cursor status:', error);
    }
    
    // Also update global presence manager for cursor activity
    this.updateGlobalPresenceManagerCursor(true);
  }

  /**
   * Handle cursor leaving the document (user clicks away)
   */
  handleCursorLeave() {
    // tdLog('🖱️ Cursor left document - user clicked away');
    
    // Clear cursor activity in WebSocket awareness
    try {
      if (window.wsProvider && window.wsProvider.awareness) {
        window.wsProvider.awareness.setLocalStateField('cursor_active', false);
        // tdLog('🔌 WebSocket awareness cursor_active set to false');
      }
    } catch (error) {
      console.error('❌ Error clearing WebSocket awareness cursor status:', error);
    }
    
    // Update global presence manager to show cursor is no longer active
    this.updateGlobalPresenceManagerCursor(false);
  }

  /**
   * Set up presence channel for real-time updates
   * DISABLED: Using WebSocket awareness instead to prevent conflicts
   */
  async setupPresenceChannel() {
    // tdLog('🔌 Supabase presence channel disabled - using WebSocket awareness only');
    // tdLog('🔌 Typing detection: Presence channel setup skipped (using WebSocket awareness)');
    // Supabase presence system disabled to prevent conflicts with WebSocket awareness
    return;
  }

  /**
   * Broadcast typing status to other users
   * @param {boolean} isTyping - Whether user is currently typing
   */
  async broadcastTypingStatus(isTyping) {
    // DISABLED: Supabase presence broadcasting - using WebSocket awareness only
    // tdLog('🔌 Supabase presence broadcasting disabled - using WebSocket awareness only');
    
    // Update WebSocket awareness
    this.updateWebSocketAwareness(isTyping);
    
    // Also update global presence manager for typing indicators
    this.updateGlobalPresenceManager(isTyping);
    
    // CRITICAL: If user is starting to type, clear any other users' typing status
    // This prevents the "both users typing" issue
    if (isTyping && this.currentUser) {
      this.clearOtherUsersTypingStatus();
    }
  }
  
  /**
   * Update WebSocket awareness with typing status
   * @param {boolean} isTyping - Whether user is currently typing
   */
  updateWebSocketAwareness(isTyping) {
    try {
      // Get the WebSocket provider from the global scope
      if (window.wsProvider && window.wsProvider.awareness) {
        window.wsProvider.awareness.setLocalStateField('is_typing', isTyping);
      }
    } catch (error) {
      console.error('❌ Error updating WebSocket awareness typing status:', error);
    }
  }

  /**
   * Update global presence manager with typing status
   * @param {boolean} isTyping - Whether user is currently typing
   */
  updateGlobalPresenceManager(isTyping) {
    try {
      // Import global presence manager dynamically to avoid circular dependencies
      import('./globalPresenceManager').then(({ default: globalPresenceManager }) => {
        if (globalPresenceManager && this.currentUser && this.currentDocument) {
          // Get current user state to preserve other fields
          const currentUserState = globalPresenceManager.activeUsers.get(this.currentUser.id);
          if (currentUserState) {
            // Check if the typing status is actually changing to avoid unnecessary updates
            const currentTypingStatus = currentUserState.is_typing === true;
            const newTypingStatus = isTyping === true;
            
            if (currentTypingStatus !== newTypingStatus) {
              // CRITICAL: Always explicitly set typing status, never leave it undefined
              const updatedUserState = {
                ...currentUserState,
                is_typing: newTypingStatus, // Explicitly true or false, never undefined
                last_activity: new Date().toISOString()
              };
              
              globalPresenceManager.activeUsers.set(this.currentUser.id, updatedUserState);
              
              // Notify subscribers of the typing status change
              globalPresenceManager.notifySubscribers();
            }
          } else {
            console.warn('⚠️ Current user state not found in global presence manager:', this.currentUser);
          }
        }
      }).catch(error => {
        console.error('❌ Error importing global presence manager:', error);
      });
    } catch (error) {
      console.error('❌ Error updating global presence manager typing status:', error);
    }
  }

  /**
   * Clear typing status for all other users (not the current user)
   * This prevents the "both users typing" issue
   */
  clearOtherUsersTypingStatus() {
    try {
      import('./globalPresenceManager').then(({ default: globalPresenceManager }) => {
        if (globalPresenceManager && this.currentUser) {
          let hasChanges = false;
          const clearedUsers = [];
          
          // Clear typing status for all users except the current user
          for (const [userId, userState] of globalPresenceManager.activeUsers) {
            if (userId !== this.currentUser.id && userState.is_typing === true) {
              tdLog(`🧹 Clearing typing status for other user ${userState.name}`);
              globalPresenceManager.activeUsers.set(userId, {
                ...userState,
                is_typing: false,
                last_activity: new Date().toISOString()
              });
              clearedUsers.push(userState.name);
              hasChanges = true;
            }
          }
          
          if (hasChanges) {
            globalPresenceManager.notifySubscribers();
            tdLog('🧹 Cleared typing status for other users:', clearedUsers);
          }
        }
      }).catch(error => {
        console.error('❌ Error clearing other users typing status:', error);
      });
    } catch (error) {
      console.error('❌ Error clearing other users typing status:', error);
    }
  }

  /**
   * Force clear all typing status for all users
   * This is a failsafe to prevent ghost typing indicators
   */
  forceClearAllTypingStatus() {
    try {
      import('./globalPresenceManager').then(({ default: globalPresenceManager }) => {
        if (globalPresenceManager) {
          let hasChanges = false;
          const clearedUsers = [];
          
          // Clear typing status for all users
          for (const [userId, userState] of globalPresenceManager.activeUsers) {
            if (userState.is_typing === true) {
              tdLog(`🧹 Force clearing typing status for ${userState.name}`);
              globalPresenceManager.activeUsers.set(userId, {
                ...userState,
                is_typing: false,
                last_activity: new Date().toISOString()
              });
              clearedUsers.push(userState.name);
              hasChanges = true;
            }
          }
          
          if (hasChanges) {
            globalPresenceManager.notifySubscribers();
            // tdLog('🧹 Force cleared all typing status for users:', clearedUsers);
          } else {
            // tdLog('🧹 No typing status to clear');
          }
        }
      }).catch(error => {
        console.error('❌ Error force clearing typing status:', error);
      });
    } catch (error) {
      console.error('❌ Error force clearing typing status:', error);
    }
  }

  /**
   * Update global presence manager with cursor activity
   * @param {boolean} hasCursor - Whether user has cursor active
   */
  updateGlobalPresenceManagerCursor(hasCursor) {
    try {
      // Import global presence manager dynamically to avoid circular dependencies
      import('./globalPresenceManager').then(({ default: globalPresenceManager }) => {
        if (globalPresenceManager && this.currentUser && this.currentDocument) {
          // Get current user state to preserve other fields
          const currentUserState = globalPresenceManager.activeUsers.get(this.currentUser.id);
          if (currentUserState) {
            // CRITICAL: Always explicitly set cursor status, never leave it undefined
            const updatedUserState = {
              ...currentUserState,
              cursor_active: hasCursor === true, // Explicitly true or false, never undefined
              last_activity: new Date().toISOString()
            };
            
            globalPresenceManager.activeUsers.set(this.currentUser.id, updatedUserState);
            
            // Notify subscribers of the cursor status change
            globalPresenceManager.notifySubscribers();
            
            tdLog('🔌 Global presence manager cursor status updated:', {
              hasCursor,
              currentUserState: updatedUserState
            });
          } else {
            console.warn('⚠️ Current user state not found in global presence manager for cursor update:', this.currentUser);
          }
        }
      }).catch(error => {
        console.error('❌ Error importing global presence manager:', error);
      });
    } catch (error) {
      console.error('❌ Error updating global presence manager cursor status:', error);
    }
  }

  /**
   * Broadcast cursor position
   */
  async broadcastCursorPosition() {
    if (!this.presenceChannel || !this.currentUser) return;

    try {
      // Throttle cursor updates to avoid spam
      const now = Date.now();
      if (this.lastCursorUpdate && (now - this.lastCursorUpdate) < 2000) {
        return; // Skip if updated less than 2000ms ago
      }
      this.lastCursorUpdate = now;

      await this.presenceChannel.track({
        user_id: this.currentUser.id,
        name: this.currentUser.name,
        is_typing: this.isTyping,
        last_activity: new Date().toISOString(),
        document: this.currentDocument,
        cursor_active: true
      });
    } catch (error) {
      console.error('❌ Error broadcasting cursor position:', error);
    }
  }

  /**
   * Handle presence sync events
   */
  handlePresenceSync() {
    if (!this.presenceChannel) return;

    // Debounce presence updates to avoid spam
    if (this.presenceUpdateTimeout) {
      clearTimeout(this.presenceUpdateTimeout);
    }

    this.presenceUpdateTimeout = setTimeout(() => {
      const presenceState = this.presenceChannel.presenceState();
      const allUsers = Object.values(presenceState).flat();
      
      // Emit typing status update event
      this.emitTypingUpdate(allUsers);
    }, 1000); // 1000ms debounce
  }

  /**
   * Handle user join events
   * @param {Array} newPresences - New user presences
   */
  handleUserJoin(newPresences) {
    // tdLog('👤 User joined document:', newPresences);
    // Don't immediately sync to avoid spam
    setTimeout(() => {
      this.handlePresenceSync();
    }, 1000); // Increased delay to 1000ms
  }

  /**
   * Handle user leave events
   * @param {Array} leftPresences - Users who left
   */
  handleUserLeave(leftPresences) {
    // tdLog('👋 User left document:', leftPresences);
    // Don't immediately sync to avoid spam
    setTimeout(() => {
      this.handlePresenceSync();
    }, 1000); // Increased delay to 1000ms
  }

  /**
   * Emit typing update event
   * @param {Array} users - All current users
   */
  emitTypingUpdate(users) {
    // Deduplicate users by user_id to prevent duplicate entries
    const uniqueUsers = users.reduce((acc, user) => {
      const existingUser = acc.find(u => u.user_id === user.user_id);
      if (existingUser) {
        // Keep the most recent user data
        const index = acc.findIndex(u => u.user_id === user.user_id);
        acc[index] = user;
      } else {
        acc.push(user);
      }
      return acc;
    }, []);
    
    // Only include users who are actually typing (not just present)
    const typingUsers = uniqueUsers.filter(user => user.is_typing === true);
    const activeUsers = uniqueUsers.filter(user => user.cursor_active === true);
    
    // Debug logging to see what's happening
    tdLog('🔍 Typing detection debug:');
    tdLog('  - All users count:', users.length);
    users.forEach((u, i) => {
      tdLog(`    [${i}] ${u.name} (${u.user_id}): is_typing=${u.is_typing}, cursor_active=${u.cursor_active}, last_activity=${u.last_activity}`);
    });
    
    tdLog('  - Unique users count:', uniqueUsers.length);
    uniqueUsers.forEach((u, i) => {
      tdLog(`    [${i}] ${u.name} (${u.user_id}): is_typing=${u.is_typing}, cursor_active=${u.cursor_active}, last_activity=${u.last_activity}`);
    });
    
    tdLog('  - Typing users count:', typingUsers.length);
    typingUsers.forEach((u, i) => {
      tdLog(`    [${i}] ${u.name} (${u.user_id}): is_typing=${u.is_typing}`);
    });
    
    tdLog('  - Active users count:', activeUsers.length);
    activeUsers.forEach((u, i) => {
      tdLog(`    [${i}] ${u.name} (${u.user_id}): cursor_active=${u.cursor_active}`);
    });
    
    // Show detailed typing status for each user
    tdLog('🔍 Detailed typing status:');
    uniqueUsers.forEach(user => {
      tdLog(`  - User ${user.name} (${user.user_id}): is_typing=${user.is_typing}, cursor_active=${user.cursor_active}, last_activity=${user.last_activity}, document=${user.document}`);
    });
    
    // Show which users are actually typing vs just present
    tdLog('🔍 Typing vs Presence Analysis:');
    uniqueUsers.forEach(user => {
      const isActuallyTyping = user.is_typing === true;
      const isJustPresent = user.is_typing === undefined || user.is_typing === false;
      tdLog(`  - ${user.name}: Actually typing=${isActuallyTyping}, Just present=${isJustPresent}, Raw is_typing=${user.is_typing}`);
    });
    
    // Dispatch custom event for components to listen to
    window.dispatchEvent(new CustomEvent('typingUpdate', {
      detail: {
        typingUsers,
        activeUsers,
        allUsers: uniqueUsers
      }
    }));
  }

  /**
   * Clean up typing detection
   */
  cleanup() {
    tdLog('🧹 Cleaning up typing detection...');
    
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    if (this.presenceUpdateTimeout) {
      clearTimeout(this.presenceUpdateTimeout);
    }

    if (this.initialLoadTimeout) {
      clearTimeout(this.initialLoadTimeout);
    }

    // Broadcast that user is no longer typing before cleanup
    if (this.isTyping && this.currentUser) {
      tdLog('🛑 User was typing - broadcasting stop typing status');
      this.broadcastTypingStatus(false);
    } else if (this.currentUser) {
      // Even if not currently typing, ensure we clear any lingering typing status
      tdLog('🛑 Clearing any lingering typing status');
      this.broadcastTypingStatus(false);
    }

    // Clear WebSocket awareness typing and cursor status
    try {
      if (window.wsProvider && window.wsProvider.awareness) {
        window.wsProvider.awareness.setLocalStateField('is_typing', false);
        window.wsProvider.awareness.setLocalStateField('cursor_active', false);
        tdLog('🔌 WebSocket awareness typing and cursor status cleared');
      }
    } catch (error) {
      console.error('❌ Error clearing WebSocket awareness status:', error);
    }

    // Also clear cursor status in global presence manager
    if (this.currentUser) {
      this.updateGlobalPresenceManagerCursor(false);
    }

    if (this.presenceChannel) {
      this.presenceChannel.unsubscribe();
      this.presenceChannel = null;
    }

    this.isTyping = false;
    this.currentUser = null;
    this.currentDocument = null;
    this.currentEditor = null;
    this.lastBroadcastedTyping = null;
    this.lastCursorUpdate = null;
    this.isInitialized = false;
    this.lastTypingBroadcast = null;
    this.lastCursorMovement = null;
    this.documentLoadComplete = false;
    this.initialLoadTimeout = null;
    this.userHasInteracted = false;
    
    tdLog('✅ Typing detection cleanup complete');
  }
}

// Create singleton instance
const typingDetection = new TypingDetection();

export default typingDetection;
